import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Clock, ListTree, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { getTaskUrgency } from "@/lib/task-urgency";
import {
  useMyAssignedSubtasks,
  type MyAssignedSubtask,
} from "@/hooks/useMyAssignedSubtasks";
import type { TaskFilter } from "@/hooks/useWorkspaceTasks";

interface MySubtasksSectionProps {
  workspaceId: string | null;
  memberId: string | null;
  filter: TaskFilter;
  onChanged?: () => void;
}

/**
 * Seção "Subtarefas atribuídas a mim" na aba Minhas. Lista as subtarefas em que
 * o membro é responsável (mesmo que a tarefa-pai seja de outra pessoa), com o
 * pai como contexto e a possibilidade de concluir/reabrir cada uma.
 */
export function MySubtasksSection({
  workspaceId,
  memberId,
  filter,
  onChanged,
}: MySubtasksSectionProps) {
  const { subtasks, loading, toggleMyCompletion } = useMyAssignedSubtasks({
    workspaceId,
    memberId,
    filter,
  });
  const { toast } = useToast();

  const handleToggle = async (subtask: MyAssignedSubtask) => {
    try {
      const completed = Boolean(subtask.my_completed_at);
      await toggleMyCompletion(subtask.id, !completed);
      onChanged?.();
    } catch (err: unknown) {
      toast({
        title: "Erro ao atualizar subtarefa",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  if (loading && subtasks.length === 0) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (subtasks.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <ListTree className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-medium">
          Subtarefas atribuídas a mim ({subtasks.length})
        </h2>
      </div>

      <div className="space-y-2">
        {subtasks.map((subtask) => {
          const isCompleted = Boolean(subtask.my_completed_at);
          const urgency = getTaskUrgency({
            status: subtask.status === "cancelled" ? "pending" : subtask.status,
            due_date: subtask.due_date,
            due_at: subtask.due_at,
          });

          return (
            <Card
              key={subtask.id}
              className={cn(
                "flex items-start gap-3 p-3 transition-colors",
                isCompleted && "opacity-70",
                urgency.cardClassName,
              )}
            >
              <Checkbox
                checked={isCompleted}
                onCheckedChange={() => void handleToggle(subtask)}
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn(
                      "text-sm font-medium",
                      isCompleted && "text-muted-foreground line-through",
                    )}
                  >
                    {subtask.title}
                  </span>
                  {urgency.label && (
                    <span
                      className={cn(
                        "rounded-full border px-2 py-0.5 text-[11px] leading-none",
                        urgency.badgeClassName,
                      )}
                    >
                      {urgency.label}
                    </span>
                  )}
                </div>
                {subtask.description && (
                  <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                    {subtask.description}
                  </p>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {subtask.due_date
                      ? format(parseISO(subtask.due_date), "dd 'de' MMM", { locale: ptBR })
                      : "Sem data"}
                    {subtask.due_at &&
                      ` · ${format(parseISO(subtask.due_at), "HH:mm", { locale: ptBR })}`}
                  </span>
                  {subtask.parentTitle && (
                    <span className="truncate">Tarefa: {subtask.parentTitle}</span>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
